import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import ViverseButton from '@/components/ViverseButton'
import { Asset } from '@/types/asset'
import Image from 'next/image'
import { ChangeEvent, JSX, RefObject, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import IconDelete from '@/components/assets/icon_delete.svg'
import IconPlus from '@/components/assets/icon_plus.svg'
import IconVideo from '@/components/assets/icon_video.svg'
import ProcessingOverlay from '@/components/ProcessingOverlay'
import { WorldSettingsFormValues } from '@/hooks/useWorldSettingsFormSchema'
import { useTranslations } from 'next-intl'

const ACCEPTED_FILE_TYPES_VIDEO = ['video/mp4'] as const
const ACCEPTED_FILE_TYPES_IMAGE = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'] as const
const MAX_FILE_SIZE_VIDEO = '25MB'
const MAX_FILE_SIZE_IMAGE = '2MB'

export type WorldSettingMediaTabProps = {
  isPublicPolicy: boolean
  renderRequiredDot: () => JSX.Element
  isLoadingTrailerMedia?: boolean
  videoFileInput: RefObject<HTMLInputElement | null>
  imageFileInput: RefObject<HTMLInputElement | null>
  videoAssets: Asset[]
  imageAssets: Asset[]
  handleVideoFileChange: (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (files: File[]) => void,
  ) => void
  handleImageFileChange: (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (files: (File | { id?: string; url?: string })[]) => void,
  ) => void
  handleDeleteVideo: (index: number, url: string) => void
  handleDeleteImage: (index: number, url: string) => void
}

const WorldSettingMediaTab = ({
  isPublicPolicy,
  renderRequiredDot,
  isLoadingTrailerMedia,
  videoFileInput,
  imageFileInput,
  videoAssets,
  imageAssets,
  handleVideoFileChange,
  handleImageFileChange,
  handleDeleteVideo,
  handleDeleteImage,
}: WorldSettingMediaTabProps) => {
  const t = useTranslations()
  const [isDragActive, setIsDragActive] = useState(false)
  const form = useFormContext<WorldSettingsFormValues>()
  const formErrors = form.formState.errors
  const formValues = form.watch()
  const { trailerMedia } = formValues
  const hasTrailerMedia =
    (trailerMedia?.trailer_videos && trailerMedia.trailer_videos.length > 0) ||
    (trailerMedia?.trailer_images && trailerMedia.trailer_images.length > 0)

  const handleClickUploadVideoButton = () => {
    videoFileInput.current?.click()
  }

  const handleClickUploadImageButton = () => {
    imageFileInput.current?.click()
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="md:subheader-1 subheader-2">
        {t('profile-page.my-worlds-card-settings-trailer')}
        {isPublicPolicy && formErrors.trailerMedia && !hasTrailerMedia && (
          <p className="text-vive-error caption">
            {t('profile-page.my-worlds-card-settings-trailer-error-message')}
          </p>
        )}
      </div>
      <p className="text-vive-primary-30 body-2">
        {t('profile-page.my-worlds-card-settings-trailer-caption')}
      </p>

      {!isLoadingTrailerMedia && (
        <>
          <FormField
            control={form.control}
            name="trailerMedia.trailer_videos"
            render={({ field: { onChange } }) => (
              <FormItem className="w-full">
                <FormLabel className="subheader-3 text-vive-primary-20">
                  {t('profile-page.my-worlds-card-settings-trailer-video')}
                  {isPublicPolicy && formErrors.trailerMedia && renderRequiredDot()}
                </FormLabel>
                {formErrors.trailerMedia?.trailer_videos && (
                  <p className="caption text-vive-error">
                    {t('profile-page.my-worlds-card-settings-trailer-video-restrictions', {
                      size: MAX_FILE_SIZE_VIDEO,
                    })}
                  </p>
                )}
                <p className="text-vive-primary-30 caption mb-4">
                  {t('profile-page.my-worlds-card-settings-trailer-video-caption')}
                </p>
                <FormControl>
                  <div className="bg-vive-primary-70/50 flex w-full items-center justify-center rounded-[15px] p-3 backdrop-blur-[50px] md:p-4">
                    <div className="flex w-full flex-col items-center gap-2">
                      <div
                        className={`flex h-auto min-h-[174px] w-full flex-col items-center justify-center rounded-[7px] px-4 pt-2 pb-4 backdrop-blur-[50px] md:h-[300px] ${isDragActive ? 'border-vive-white bg-vive-primary-50/30 border-[1.5px] border-dashed' : 'bg-vive-primary-50/50'}`}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={() => setIsDragActive(true)}
                        onDragLeave={() => setIsDragActive(false)}
                        onDrop={(e) => {
                          e.preventDefault()
                          setIsDragActive(false)
                          if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                            // 建立一個模擬的 event 物件
                            const fakeEvent = {
                              target: {
                                files: e.dataTransfer.files,
                              },
                            } as unknown as ChangeEvent<HTMLInputElement>

                            handleVideoFileChange(fakeEvent, onChange)
                          }
                        }}
                      >
                        {videoAssets.length > 0 ? (
                          <>
                            {videoAssets.map((asset, index) => {
                              return (
                                <div
                                  className="relative mb-2 h-auto w-full md:w-[326px]"
                                  key={index}
                                >
                                  <video
                                    src={asset.video?.url}
                                    controls
                                    className="h-full w-full object-contain"
                                  />
                                  {((asset.video?.vodId && asset.video.isConverted) ||
                                    (!asset.video?.vodId && !asset.video?.fileId)) && (
                                    <ViverseButton
                                      type="button"
                                      variant="icon"
                                      size="icon"
                                      className="bg-vive-surface-normal/40 absolute top-2 right-2 h-6 w-6 backdrop-blur-[12px]"
                                      onClick={() =>
                                        handleDeleteVideo(index, asset.video?.url || '')
                                      }
                                    >
                                      <IconDelete
                                        width={16}
                                        height={16}
                                        className="fill-vive-primary-20"
                                      />
                                    </ViverseButton>
                                  )}
                                  {asset.video?.vodId && !asset.video?.isConverted && (
                                    <ProcessingOverlay
                                      animate
                                      className="pointer-events-none absolute top-0 left-0"
                                    />
                                  )}
                                </div>
                              )
                            })}
                          </>
                        ) : (
                          <>
                            <IconVideo className="mb-4 h-7 w-7 md:h-8 md:w-8" />
                            <p className="subheader-3 text-vive-primary-20 hidden md:block">
                              {t('profile-page.my-worlds-card-settings-trailer-video-drop')}
                            </p>
                          </>
                        )}

                        <p className="caption">
                          {t('profile-page.my-worlds-card-settings-trailer-video-limit')}
                        </p>
                        <Input
                          id="media"
                          type="file"
                          className="hidden"
                          accept={ACCEPTED_FILE_TYPES_VIDEO.join(',')}
                          ref={videoFileInput}
                          onChange={(event) => handleVideoFileChange(event, onChange)}
                        />
                        <ViverseButton
                          type="button"
                          variant="secondary"
                          size="small2"
                          onClick={handleClickUploadVideoButton}
                          className="text-[14p mt-4 h-[34px] md:mt-6"
                        >
                          {t('profile-page.my-worlds-card-settings-trailer-video-upload')}
                        </ViverseButton>
                      </div>
                      {/* <p>{t('profile-page.my-worlds-card-settings-trailer-video-or')}</p>
                      <div className="md:h-[48px] flex h-[34px] w-full items-center gap-4">
                        <ViverseInput
                          className="md:h-[48px] h-[34px] min-h-0 w-full border-none bg-transparent"
                          containerClassName="h-[34px] md:h-[48px] border-none bg-vive-primary-50/60 backdrop-blur-[50px]"
                          placeholder={t(
                            'profile-page.my-worlds-card-settings-trailer-video-paste-link',
                          )}
                          onChange={(e) => setUrlInput(e.target.value)}
                        />
                        <ViverseButton
                          type="button"
                          variant="secondary"
                          size="small2"
                          className="h-[34px]"
                          onClick={() => {
                            resolveVideoUrl(urlInput)
                          }}
                        >
                          {t('profile-page.my-worlds-card-settings-trailer-video-insert')}
                        </ViverseButton>
                      </div> */}
                    </div>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="trailerMedia.trailer_images"
            render={({ field: { onChange } }) => (
              <FormItem className="w-full">
                <FormLabel className="subheader-3 text-vive-primary-20">
                  {t('profile-page.my-worlds-card-settings-trailer-image')}
                  {isPublicPolicy && formErrors.trailerMedia && renderRequiredDot()}
                </FormLabel>
                {formErrors.trailerMedia?.trailer_images && (
                  <p className="caption text-vive-error">
                    {t('profile-page.my-worlds-card-settings-trailer-image-restrictions', {
                      size: MAX_FILE_SIZE_IMAGE,
                    })}
                  </p>
                )}
                <FormControl>
                  <div className="bg-vive-primary-70/50 mt-2 w-full rounded-lg p-3 backdrop-blur-[50px] md:p-4">
                    <div className="body-2 mb-2 flex flex-row justify-between md:mb-4">
                      <p className="text-vive-primary-30 body-2">
                        {t('profile-page.my-worlds-card-settings-trailer-image-limit')}
                      </p>
                      <p className="text-vive-primary-30 body-2">
                        {' '}
                        <span className="text-white"> {imageAssets.length} </span>/ 10{' '}
                        {t('profile-page.my-worlds-card-settings-trailer-image-limit-number')}
                      </p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 md:gap-3">
                      <div
                        className="bg-vive-primary-50/60 hover:bg-vive-primary-70 flex aspect-[16/9] h-auto w-full flex-col items-center justify-center rounded-[7px] backdrop-blur-[50px]"
                        onClick={handleClickUploadImageButton}
                      >
                        <div className="flex flex-col items-center justify-center text-center">
                          <IconPlus width={19} height={19} className="fill-vive-primary-20" />
                          <div className="subheader-3 text-vive-primary-20 hidden md:block">
                            {t('profile-page.my-worlds-card-settings-trailer-image-add')}
                          </div>
                          <Input
                            id="media"
                            type="file"
                            className="hidden"
                            accept={ACCEPTED_FILE_TYPES_IMAGE.join(',')}
                            ref={imageFileInput}
                            multiple
                            onChange={(event) => handleImageFileChange(event, onChange)}
                          />
                        </div>
                      </div>
                      {imageAssets.map((asset, index) => (
                        <div
                          key={`image-asset-${index}`}
                          className="relative aspect-[16/9] w-full overflow-hidden rounded-[7px]"
                        >
                          <Image
                            src={asset.image?.url || ''}
                            alt=""
                            fill
                            className="object-cover"
                          />
                          <ViverseButton
                            type="button"
                            variant="icon"
                            size="icon"
                            className="bg-vive-surface-normal/40 absolute top-2 right-2 h-6 w-6 backdrop-blur-[12px]"
                            onClick={() => handleDeleteImage(index, asset.image?.url || '')}
                          >
                            <IconDelete width={16} height={16} className="fill-vive-primary-20" />
                          </ViverseButton>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  )
}

export default WorldSettingMediaTab
